import React, {useCallback} from "react";
import {ErrorCodes, ApiErrorCodes} from "../api/consts";
import {getAxiosResponseErrorCode, getAxiosResponseErrorData, isAxiosAuthError, isStringI18n, extractI18nString} from "./utils";
import {useTranslation} from "react-i18next";
import {useSMCSnackbar} from "./hooks";

export function useSMCErrorHandler() {
    const { t } = useTranslation();
    const { enqueueLogoutSnackbar, enqueueErrorSnackbar } = useSMCSnackbar();

    const buildErrorObjectMessage = useCallback(errorObject => {
        let errorMessage = '';
        if (typeof errorObject === 'object') {
            // If error code is i18n, extract and translate it
            if (errorObject.errorCode) {
                let errorParams = errorObject.errorParams;
                if (errorParams) {
                    errorParams = errorParams.map(param => isStringI18n(param) ? t(extractI18nString(param)): param);
                }
                errorMessage = t(extractI18nString(errorObject.errorCode), errorParams);
            } else {
                errorMessage = errorObject.id;
            }
        } else {
            errorMessage = 'Error in field: ' + errorObject;
        }
        return errorMessage;
    }, [t]);

    // Extracts and builds error message from axios error
    const getAxiosErrorMessage = useCallback(error => {
        let errorMessage;

        const isTimeoutError = error.code === 'ECONNABORTED';
        // Get error code, if not found, use general error
        const errorCode = getAxiosResponseErrorCode(error) || ApiErrorCodes.GENERAL_ERROR;
        const errorData = getAxiosResponseErrorData(error);

        if (isTimeoutError) {
            errorMessage = t('SMC_TIMEOUT_ERROR');
        } else {
            switch (errorCode) {
                case ApiErrorCodes.FIELD_ERROR: {
                    errorMessage = t('com.sofaware.db.OBJECT_NOT_VALID');
                    const illegalFields = errorData.illegalFields;
                    if (Array.isArray(illegalFields)) {
                        illegalFields.forEach(field => {
                            errorMessage += '\n';
                            errorMessage += buildErrorObjectMessage(field);
                        });
                    } else if (illegalFields) {
                        errorMessage += '\n';
                        errorMessage += buildErrorObjectMessage(illegalFields);
                    }
                    break;
                }
                case ApiErrorCodes.CREATE_ERROR :
                case ApiErrorCodes.OBJ_EXISTS: {
                    errorMessage = t('com.sofaware.db.OBJECT_NOT_VALID');
                    const errorObjects = errorData.errorObjects;
                    if (Array.isArray(errorObjects)) {
                        errorObjects.forEach(errorObj => {
                            errorMessage += '\n';
                            errorMessage += buildErrorObjectMessage(errorObj);
                        })
                    } else if (errorObjects) {
                        errorMessage += '\n';
                        errorMessage += buildErrorObjectMessage(errorObjects);
                    }
                    break;
                }
                case ApiErrorCodes.GENERAL_ERROR: {
                    if (errorData && errorData.errorCode) {
                        errorMessage = t(errorData.errorCode);
                    }
                    else if (errorData && errorData.errorObjects) {
                        const errorObjects = errorData.errorObjects;
                        if (Array.isArray(errorObjects)) {
                            errorMessage = errorObjects.map(buildErrorObjectMessage).join('\n');
                        } else {
                            errorMessage = buildErrorObjectMessage(errorObjects);
                        }    
                    }
                    else {
                        errorMessage = t(ApiErrorCodes.GENERAL_ERROR);
                    }
                    break;
                }
                default: {
                    errorMessage = t(errorCode);
                    break;
                }
            }
        }

        return errorMessage;
    }, [buildErrorObjectMessage, t]);

    // Extracts and builds error message from javascript error
    const getErrorMessage = useCallback((error) => {
        if (error.isAxiosError) {
            return getAxiosErrorMessage(error);
        }
        else if (error.message in ErrorCodes) {
            return t(error.message);
        }
        // If error is not axios error or in error codes, return general error
        return t(ApiErrorCodes.GENERAL_ERROR);
    }, [getAxiosErrorMessage, t]);

    const getMessagesByPages = useCallback((error, pageIdToPageName) => {
        const messagesByPages = []; // List of errors, sepperated by pages {page:string, messages:[string]}

        const errorCode = getAxiosResponseErrorCode(error);
        const errorData = getAxiosResponseErrorData(error);

        if (errorCode === ApiErrorCodes.FIELD_ERROR && errorData) {
            let illegalFields = errorData.illegalFields;
            if (illegalFields) {
                if (!Array.isArray(illegalFields)) {
                    illegalFields = [illegalFields];
                }
                const errorObjects = []; // List of errors in objects of {page:string, message:string}
                const pages = []; // List of every page name we encounter
                
                // Build error objects and pages lists
                illegalFields.forEach(field => {
                    let pageName = undefined;
                    if (field.id && (field.id.indexOf('.') > 0)) {
                        pageName = field.id.substring(0, field.id.indexOf('.'));
                    }
                    if (pageName && !pages.includes(pageName)) {
                        pages.push(pageName);
                    }
                    errorObjects.push({
                        page: pageName,
                        message: buildErrorObjectMessage(field)
                    });
                });
                
                // Add messages for each page
                pages.forEach(pageName => {
                    const pageMessages = errorObjects.filter(errObj => errObj.page === pageName).map(errObj => errObj.message);
                    const pageErrorObject = {
                        page: pageName,
                        messages: pageMessages
                    }
                    messagesByPages.push(pageErrorObject);
                });
                // Add errors without pages
                const messagesWithoutPage = errorObjects.filter(errObj => !errObj.page).map(errObj => errObj.message);
                if (messagesWithoutPage.length) {
                    messagesByPages.push({
                        page: undefined,
                        messages: messagesWithoutPage
                    })
                }
            }
            return (
                <>
                    {
                        messagesByPages.map(pageErrors => {
                            let pageName = pageErrors.page;
                            // Try to find page name in pageIdToPageName
                            if (pageIdToPageName && pageIdToPageName[pageName]) {
                                pageName = pageIdToPageName[pageName];
                            }
                            if (pageName) {
                                return (
                                    <React.Fragment key={pageName}>
                                        Error on page {pageName}:
                                        <ul>
                                            {
                                                pageErrors.messages.map((message, index) => (
                                                    <li key={index}>{message}</li>
                                                ))
                                            }
                                        </ul>
                                    </React.Fragment>
                                )
                            }
                            // If has no page name, it is a general error
                            else {
                                return (
                                    <>
                                    General errors:
                                        <ul>
                                            {
                                                pageErrors.messages.map((message, index) => (
                                                    <li key={index}>{message}</li>
                                                ))
                                            }
                                        </ul>
                                    </>
                                )
                            }
                        })
                    }
                </>
            );
        }
        else {
            return null;
        }
    }, [buildErrorObjectMessage]);

    const getMessagesArrayByPages = useCallback((error, pageIdToPageName) => {
        const messagesByPages = []; // List of errors, sepperated by pages {pageId:string, pageName:string, messages:[string]}

        const errorCode = getAxiosResponseErrorCode(error);
        const errorData = getAxiosResponseErrorData(error);

        if (errorCode === ApiErrorCodes.FIELD_ERROR && errorData) {
            let illegalFields = errorData.illegalFields;
            if (illegalFields) {
                if (!Array.isArray(illegalFields)) {
                    illegalFields = [illegalFields];
                }
                const errorObjects = []; // List of errors in objects of {pageId:string, pageName:string, message:string}
                const pages = []; // List of every page name we encounter
                
                // Build error objects and pages lists
                illegalFields.forEach(field => {
                    let pageId = undefined;
                    if (field.id && (field.id.indexOf('.') > 0)) {
                        pageId = field.id.substring(0, field.id.indexOf('.'));
                    }
                    if (pageId && !pages.includes(pageId)) {
                        pages.push(pageId);
                    }
                    errorObjects.push({
                        pageId: pageId,
                        message: buildErrorObjectMessage(field)
                    });
                });
                
                // Add messages for each page
                pages.forEach(pageId => {
                    const pageMessages = errorObjects.filter(errObj => errObj.pageId === pageId).map(errObj => errObj.message);
                    let pageName = undefined;
                    if (pageIdToPageName && pageIdToPageName[pageId]) {
                        pageName = pageIdToPageName[pageId];
                    } else {
                        pageName = pageId;
                    }
                    const pageErrorObject = {
                        pageId: pageId,
                        pageName: pageName,
                        messages: pageMessages
                    }
                    messagesByPages.push(pageErrorObject);
                });
                // Add errors without pages
                const messagesWithoutPage = errorObjects.filter(errObj => !errObj.page).map(errObj => errObj.message);
                if (messagesWithoutPage.length) {
                    messagesByPages.push({
                        pageId: undefined,
                        messages: messagesWithoutPage
                    })
                }
            }
            return messagesByPages;
        }
        else {
            return null;
        }
    }, [buildErrorObjectMessage]);

    // Save error
    // Shows alert on error
    const handleSaveError = useCallback((error) => {
        let errorMessage = getErrorMessage(error);

        if (isAxiosAuthError(error)) {
            enqueueLogoutSnackbar(errorMessage);
        }
        else {
            alert(errorMessage);
        }
    }, [getErrorMessage, enqueueLogoutSnackbar]);

    // Editor load error
    // Show error snackbar on error
    const handleEditorLoadError = useCallback((error) => {
        let errorMessage = getErrorMessage(error);

        if (isAxiosAuthError(error)) {
            enqueueLogoutSnackbar(errorMessage);
        }
        else {
            enqueueErrorSnackbar(errorMessage);
        }
    }, [getErrorMessage, enqueueLogoutSnackbar, enqueueErrorSnackbar]);

    return {
        handleEditorLoadError,
        handleSaveError,
        getErrorMessage,
        getMessagesByPages,
        getMessagesArrayByPages
    };
}